import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getEnterpriseSetting } from "../api/settings";

export const SELLER_QUOTE_REQUESTS_SETTINGS_ID = "seller-quote-requests";

/** Module cache so nav/CTAs don't each refetch on every mount. */
let cache = {
  enterpriseId: "",
  enabled: true,
  loaded: false,
};

const listeners = new Set();

const notify = () => {
  listeners.forEach((fn) => {
    try {
      fn(cache);
    } catch {
      /* ignore */
    }
  });
};

export const setSellerQuoteRequestsEnabledCache = (enterpriseId, enabled) => {
  cache = {
    enterpriseId: String(enterpriseId || ""),
    enabled: enabled !== false,
    loaded: true,
  };
  notify();
};

/**
 * Enterprise opt-out for seller Request Quote.
 * Defaults to ON while loading / when unset so GA tenants keep the feature.
 */
export function useSellerQuoteRequestsEnabled() {
  const enterpriseId = useSelector(
    (s) => s?.currentUser?.user?.enterpriseId || ""
  );
  const [enabled, setEnabled] = useState(() =>
    cache.enterpriseId === enterpriseId ? cache.enabled : true
  );
  const [loading, setLoading] = useState(
    !(cache.loaded && cache.enterpriseId === enterpriseId)
  );

  useEffect(() => {
    const onChange = (next) => {
      if (next.enterpriseId !== String(enterpriseId || "")) return;
      setEnabled(next.enabled !== false);
      setLoading(!next.loaded);
    };
    listeners.add(onChange);
    return () => listeners.delete(onChange);
  }, [enterpriseId]);

  useEffect(() => {
    let cancelled = false;
    if (!enterpriseId) {
      setEnabled(true);
      setLoading(false);
      return undefined;
    }
    if (cache.loaded && cache.enterpriseId === enterpriseId) {
      setEnabled(cache.enabled !== false);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    getEnterpriseSetting(SELLER_QUOTE_REQUESTS_SETTINGS_ID)
      .then((res) => {
        if (cancelled) return;
        const on = res?.data?.enabled !== false;
        setSellerQuoteRequestsEnabledCache(enterpriseId, on);
        setEnabled(on);
      })
      .catch(() => {
        if (cancelled) return;
        // Fail open — feature stays available if settings can't load.
        setSellerQuoteRequestsEnabledCache(enterpriseId, true);
        setEnabled(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [enterpriseId]);

  return { enabled, loading };
}
